import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, ElementRef, EventEmitter, Inject, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { AmplitudeService } from '@inyova/inyova-shared';
import { CustomerAccount } from '@inyova/models';
import { TranslateService } from '@ngx-translate/core';
import { PageScrollService } from 'ngx-page-scroll-core';

import { select, Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as StrategyActions from '@strategy/strategy.actions';
import * as fromAppRoot from '@app/app.reducers';
import * as fromStrategy from '@strategy/reducers';

import { CompanyListItem, CustomerInvestmentData, Notification } from '@shared/models/Strategy';
import { Languages, LanguageService } from '@shared/services/language.service';
import { TrackingService } from '@shared/services/tracking.service';

@Component({
  selector: 'app-strategy-portfolio-stocks-bonds',
  templateUrl: './strategy-portfolio-stocks-bonds.component.html',
  styleUrls: ['../strategy-personalise/strategy-personalise.component.scss', './strategy-portfolio-stocks-bonds.component.scss'],
  standalone: false
})
export class StrategyPortfolioStocksBondsComponent implements OnDestroy {
  protected readonly onDestroy$ = new Subject<void>();
  @Input() appLocation: 'ch' | 'de';
  @Input() investmentData: CustomerInvestmentData;
  @Input() step: number;
  @Input() kind: '3b' | '3a' | 'kid';
  @Input() bonds: boolean;
  @Input() isEve2: boolean;
  @ViewChild('companyDetail') companyDetail: ElementRef;

  @Output() updateWishedCount = new EventEmitter<number>();
  portfolioStocks: CompanyListItem[];
  wishlistCompanies: CompanyListItem[];
  portfolioCompaniesList: CompanyListItem[];
  editMode = false;
  yourStocks = true;
  stocksCount: number;
  showCompanyDetail = false;
  selectedBondID = 0;
  selectedCompanyID = null;
  showAll = false;
  language: Languages;
  currentAccount: CustomerAccount;
  notification: Notification;
  notificationTimeout: number;
  showStabilityModal = false;
  someZeroPercentage = false;
  MINIMUM_COMPANIES = 8;
  shownCompanies = this.MINIMUM_COMPANIES;
  riskName = 'balanced';

  constructor(
    private store: Store,
    public router: Router,
    public http: HttpClient,
    private trackingService: TrackingService,
    private translateService: TranslateService,
    private languageService: LanguageService,
    private scrollService: PageScrollService,
    private amplitudeService: AmplitudeService,
    @Inject(DOCUMENT) private document: any
  ) {
    this.store.pipe(select(fromAppRoot.selectCurrentInvestAccount), takeUntil(this.onDestroy$)).subscribe((value) => {
      this.currentAccount = value;
    });

    this.store.pipe(select(fromStrategy.selectNotificationData), takeUntil(this.onDestroy$)).subscribe((notification) => {
      this.notification = notification;

      if (notification.show) {
        return;
      }
      // clear timeout
      window.clearTimeout(this.notificationTimeout);
    });

    this.language = this.languageService.selected.value;

    this.store.pipe(select(fromStrategy.selectPortfolioCompanyList), takeUntil(this.onDestroy$)).subscribe((portfolioCompanyList) => {
      this.portfolioStocks = this.sortByTopic([...portfolioCompanyList.yova_stocks]);
      this.wishlistCompanies = this.getWishlistCompanies(portfolioCompanyList.wishlist);
      this.portfolioCompaniesList = [...this.wishlistCompanies, ...this.portfolioStocks];
      this.stocksCount = this.portfolioStocks.length + this.wishlistCompanies.length;
      // TODO move to reducer
      this.someZeroPercentage = [...this.wishlistCompanies, ...this.portfolioStocks].some((company) => company.stock_ratio === 0);
      // show notification when the strategy update and the notification has detail
      if (this.notification.company && this.notification.type) {
        this.store.dispatch(StrategyActions.setNotificationShowStatus({ show: true }));

        // use window.setTimeOut because it returns number
        this.notificationTimeout = window.setTimeout(() => {
          this.store.dispatch(StrategyActions.setNotification({ notification: { company: '', type: '', show: false } }));
        }, 3000);
      }

      // show only first time...
      if (this.selectedCompanyID) {
        this.store.dispatch(StrategyActions.getCompanyDetail({ companyId: this.selectedCompanyID }));
        return;
      }
    });

    this.store.pipe(select(fromAppRoot.selectCurrentAccountEditMode), takeUntil(this.onDestroy$)).subscribe((editMode) => {
      this.editMode = editMode;
    });

    this.store.pipe(select(fromStrategy.selectRiskName), takeUntil(this.onDestroy$)).subscribe((value) => {
      this.riskName = value.riskName;
    });
  }

  trackTooltip(name: string) {
    this.amplitudeService.trackEvent('Tooltip Info Consulted', {
      tooltip_name: name,
      screen: 'portfolio stocks',
      consultation_method: 'hover'
    });
  }

  getWishlistCompanies(list: CompanyListItem[]): CompanyListItem[] {
    const wishlistNodiscarded = list.filter((wish) => wish['is_included'] !== false);
    return this.sortByTopic(wishlistNodiscarded);
  }

  sortByTopic(list: CompanyListItem[]): CompanyListItem[] {
    if (list.length === 0) {
      return [];
    }

    return list.sort((a: CompanyListItem, b: CompanyListItem) => {
      if (a.criteria_dismatch?.length || b.criteria_dismatch?.length) {
        return b.criteria_dismatch.length - a.criteria_dismatch.length;
      } else {
        return b.topics.length - a.topics.length;
      }
    });
  }

  changeTab(type: string): void {
    this.yourStocks = type === 'stock';
    this.selectedCompanyID = null;
    this.showCompanyDetail = false;
    this.trackingService.trackActivity(`[Tab] ${type}`);
  }

  updateCompanyDetail(event: { companyID: number; updated: boolean }): void {
    this.selectedCompanyID = event.companyID;
    this.showCompanyDetail = event.updated;

    if (!event.updated) {
      return;
    }

    this.store.dispatch(StrategyActions.getCompanyDetail({ companyId: this.selectedCompanyID }));

    setTimeout(() => {
      this.scrollService.scroll({
        document: this.document,
        scrollTarget: this.companyDetail.nativeElement,
        duration: 100,
        scrollOffset: 120
      });
    }, 500);
  }

  changeSelectedBondID(bondID: number): void {
    if (this.selectedBondID === bondID) {
      this.selectedBondID = 0;
      return;
    }
    this.selectedBondID = bondID;
    const bondTitle: string = this.translateService.instant(`bonds.${bondID}.title`);
    this.trackingService.trackActivity(`[List] Bonds ${bondTitle}`);
  }

  backToMatches(where: string): void {
    this.trackingService.trackActivity(`[Link] To company wishlist from ${where}`);
    this.tabNavigation(4);
  }

  tabNavigation(tabIndex: number): void {
    this.store.dispatch(StrategyActions.setTabIndex({ tabIndex }));
  }

  trackActivity(activity: string): void {
    this.trackingService.trackActivity(activity);
  }

  showAllCompanies(): void {
    this.showAll = !this.showAll;
    this.shownCompanies = this.showAll ? this.portfolioCompaniesList.length : this.MINIMUM_COMPANIES;
    this.trackActivity('[Link] Show ' + (this.showAll ? 'all' : 'fewer') + ' companies');
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}
